import React from "react";
import { t } from "@auto/services";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";

import KpiCard from "@src/reporting/components/KpiCard";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import {
  isCorrectionFilter,
  reportingResourceKey,
} from "@src/reporting/model/reportingFilters.mjs";
import { count, dateTime, percent } from "@src/reporting/model/reportingFormatters.mjs";
import {
  loadActiveCentersKpi,
  loadActiveOperatorsKpi,
  loadCapacityUtilizationKpi,
  loadConfiguredCentersKpi,
  loadDistinctApplicantsKpi,
  loadFirstApplicationKpi,
  loadLastApplicationKpi,
  loadRegisteredVotersKpi,
} from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const displayResource = (resource, formatter) => {
  if (resource.loading) return "…";
  if (resource.error) return "—";
  return formatter(resource.data || {});
};

const resourceDetail = (resource, formatter = null) => {
  if (resource.error) return resource.error;
  if (resource.loading || !formatter) return null;
  return formatter(resource.data || {});
};

const ReportingSummaryWidget = ({ filter, refreshToken = 0, compact = false }) => {
  const correction = isCorrectionFilter(filter);

  const distinctApplicants = useReportingResource(
    () => loadDistinctApplicantsKpi(filter),
    {
      key: reportingResourceKey("kpi-distinct-applicants", filter),
      refreshToken,
      enabled: correction || !compact,
      initialData: { value: 0 },
    },
  );

  const registeredVoters = useReportingResource(
    () => loadRegisteredVotersKpi(filter),
    {
      key: reportingResourceKey("kpi-registered-voters", filter),
      refreshToken,
      enabled: !correction,
      initialData: { value: 0 },
    },
  );

  const activeOperators = useReportingResource(
    () => loadActiveOperatorsKpi(filter),
    {
      key: reportingResourceKey("kpi-active-operators", filter),
      refreshToken,
      initialData: { value: 0 },
    },
  );

  const activeCenters = useReportingResource(
    () => loadActiveCentersKpi(filter),
    {
      key: reportingResourceKey("kpi-active-centers", filter),
      refreshToken,
      initialData: { value: 0 },
    },
  );

  const configuredCenters = useReportingResource(
    () => loadConfiguredCentersKpi(filter),
    {
      key: reportingResourceKey("kpi-configured-centers", filter),
      refreshToken,
      enabled: !correction,
      initialData: { value: 0 },
    },
  );

  const capacityUtilization = useReportingResource(
    () => loadCapacityUtilizationKpi(filter),
    {
      key: reportingResourceKey("kpi-capacity-utilization", filter),
      refreshToken,
      enabled: !correction,
      initialData: { value: null, totalCapacity: 0, registeredVoterCount: 0 },
    },
  );

  const firstApplication = useReportingResource(
    () => loadFirstApplicationKpi(filter),
    {
      key: reportingResourceKey("kpi-first-application", filter),
      refreshToken,
      enabled: correction,
      initialData: { value: null },
    },
  );

  const lastApplication = useReportingResource(
    () => loadLastApplicationKpi(filter),
    {
      key: reportingResourceKey("kpi-last-application", filter),
      refreshToken,
      initialData: { value: null },
    },
  );

  const standardCards = compact
    ? [
        {
          Icon: HowToRegOutlinedIcon,
          label: t("reporting.registeredVoters"),
          value: displayResource(registeredVoters, (data) => count(data.value)),
          detail: resourceDetail(registeredVoters),
        },
        {
          Icon: GroupsOutlinedIcon,
          label: t("reporting.activeOperators"),
          value: displayResource(activeOperators, (data) => count(data.value)),
          detail: resourceDetail(activeOperators),
        },
        {
          Icon: LocationOnOutlinedIcon,
          label: t("reporting.activeCenters"),
          value: displayResource(activeCenters, (data) => count(data.value)),
          detail: resourceDetail(activeCenters)
            || resourceDetail(configuredCenters, (data) => `${count(data.value)} ${t("reporting.configured")}`),
        },
        {
          Icon: MapOutlinedIcon,
          label: t("reporting.capacityUtilization"),
          value: displayResource(capacityUtilization, (data) => percent(data.value)),
          detail: resourceDetail(capacityUtilization, (data) => `${count(data.totalCapacity)} ${t("reporting.totalCapacity")}`),
        },
        {
          Icon: CalendarMonthOutlinedIcon,
          label: t("reporting.lastApplication"),
          value: displayResource(lastApplication, (data) => dateTime(data.value)),
          detail: resourceDetail(lastApplication),
        },
      ]
    : [
        {
          Icon: PersonSearchOutlinedIcon,
          label: t("reporting.applicants"),
          value: displayResource(distinctApplicants, (data) => count(data.value)),
          detail: resourceDetail(distinctApplicants),
        },
        {
          Icon: HowToRegOutlinedIcon,
          label: t("reporting.registeredVoters"),
          value: displayResource(registeredVoters, (data) => count(data.value)),
          detail: resourceDetail(registeredVoters),
        },
        {
          Icon: GroupsOutlinedIcon,
          label: t("reporting.activeOperators"),
          value: displayResource(activeOperators, (data) => count(data.value)),
          detail: resourceDetail(activeOperators),
        },
        {
          Icon: LocationOnOutlinedIcon,
          label: t("reporting.activeCenters"),
          value: displayResource(activeCenters, (data) => count(data.value)),
          detail: resourceDetail(activeCenters),
        },
        {
          Icon: LocationOnOutlinedIcon,
          label: t("reporting.configuredCenters"),
          value: displayResource(configuredCenters, (data) => count(data.value)),
          detail: resourceDetail(configuredCenters),
        },
        {
          Icon: MapOutlinedIcon,
          label: t("reporting.capacityUtilization"),
          value: displayResource(capacityUtilization, (data) => percent(data.value)),
          detail: resourceDetail(capacityUtilization, (data) => `${count(data.totalCapacity)} ${t("reporting.totalCapacity")}`),
        },
        {
          Icon: CalendarMonthOutlinedIcon,
          label: t("reporting.lastApplication"),
          value: displayResource(lastApplication, (data) => dateTime(data.value)),
          detail: resourceDetail(lastApplication),
        },
      ];

  const correctionCards = [
    {
      Icon: PersonSearchOutlinedIcon,
      label: t("reporting.applicants"),
      value: displayResource(distinctApplicants, (data) => count(data.value)),
      detail: resourceDetail(distinctApplicants),
    },
    {
      Icon: GroupsOutlinedIcon,
      label: t("reporting.activeOperators"),
      value: displayResource(activeOperators, (data) => count(data.value)),
      detail: resourceDetail(activeOperators),
    },
    {
      Icon: LocationOnOutlinedIcon,
      label: t("reporting.activeCenters"),
      value: displayResource(activeCenters, (data) => count(data.value)),
      detail: resourceDetail(activeCenters),
    },
    {
      Icon: CalendarMonthOutlinedIcon,
      label: t("reporting.firstRequest"),
      value: displayResource(firstApplication, (data) => dateTime(data.value)),
      detail: resourceDetail(firstApplication),
    },
    {
      Icon: CalendarMonthOutlinedIcon,
      label: t("reporting.lastRequest"),
      value: displayResource(lastApplication, (data) => dateTime(data.value)),
      detail: resourceDetail(lastApplication),
    },
  ];

  const cards = correction ? correctionCards : standardCards;

  return (
    <ReportingWidget
      title={correction ? t("reporting.correctionHighlights") : t("reporting.registrationHighlights")}
      subtitle={correction
        ? t("reporting.correctionHighlightsSubtitle")
        : t("reporting.registrationHighlightsSubtitle")}
    >
      <div className={compact ? styles.kpiGridCompact : styles.kpiGrid}>
        {cards.map(({ Icon, label, value, detail }) => (
          <KpiCard
            key={label}
            icon={Icon}
            label={label}
            value={value}
            detail={detail}
          />
        ))}
      </div>
    </ReportingWidget>
  );
};

export default ReportingSummaryWidget;
